import { IconUpload } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import LoaderSvg from '../../assets/images/loader.svg';
import CustomSelect from '../../components/common/CustomSelect/CustomSelect';
import SlidingButton from '../../components/common/SlidingButton/SlidingButton';
import DashboardWrapper from '../../components/dashboard/dashboardWrapper/DashboardWrapper';
import DeleteModal from '../../components/dashboard/submissions/delete-modal/delete-modal';
import EditResubmitModal from '../../components/dashboard/submissions/edit-resubmit-modal/edit-resubmit-modal';
import EmptyContentState from '../../components/dashboard/submissions/empty-content-state/empty-content-state';
import SubmissionCard from '../../components/dashboard/submissions/submission-card/submission-card';
import UploadModal from '../../components/dashboard/submissions/upload-modal/upload-modal';
import ViewModal from '../../components/dashboard/submissions/view-modal/view-modal';
import { deleteContentSubmission, getContentComments, getContentSubmissions } from '../../services/dashboard.service';
import dashboardStyles from '../../styles/dashboard.module.css';
import styles from '../../styles/submissions.module.css';
import { Comment, ContentSubmission, ContentSubmissionStage } from '../../types/content-submissions';
import ButtonComponent from '../../components/common/button-component/button-component';
import { TABS } from '../../utils/constants';

export default function Submissions() {
  const [activeTab, setActiveTab] = useState<ContentSubmissionStage>('pending');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedSubmission, setSelectedSubmission] = useState<ContentSubmission | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isResubmitModalOpen, setIsResubmitModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: submissions, isLoading } = useQuery({
    queryKey: ['submissions', activeTab],
    queryFn: async () => {
      const response = await getContentSubmissions(activeTab);
      if (!response.status) {
        throw new Error(response.message || 'Failed to fetch submissions');
      }
      return response.data;
    },
  });

  const { data: comments, isLoading: isLoadingComments } = useQuery<Comment[]>({
    queryKey: ['comments', selectedSubmission?._id, isViewModalOpen],
    queryFn: async () => {
      if (!selectedSubmission?._id) return [];
      const response = await getContentComments(selectedSubmission._id);
      if (!response.status) {
        throw new Error(response.message || 'Failed to fetch comments');
      }
      return response.data;
    },
    refetchInterval: 20000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabFromQuery = params.get('tab');
    if (tabFromQuery && Object.values(TABS).some((tab) => tab.value === tabFromQuery)) {
      setActiveTab(tabFromQuery as ContentSubmissionStage);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleTabChange = (newTab: ContentSubmissionStage) => {
    setActiveTab(newTab);
    queryClient.invalidateQueries({ queryKey: ['submissions', newTab] });

    navigate(`/dashboard/submissions`);

    if (newTab === 'approved' || newTab === 'changesRequested') {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  };

  const deleteSubmissionMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await deleteContentSubmission(id);
      if (!response.status) {
        throw new Error(response.message || 'Failed to delete submission');
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      setIsDeleteModalOpen(false);
      setSelectedSubmission(null);
      toast.success('Submission deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error deleting submission');
    },
  });

  const handleEdit = (submission: ContentSubmission) => {
    setSelectedSubmission(submission);
    setIsEditModalOpen(true);
  };

  const handleDelete = (submission: ContentSubmission) => {
    setSelectedSubmission(submission);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!selectedSubmission) return;
    deleteSubmissionMutation.mutate(selectedSubmission._id);
  };

  const handleView = (submission: ContentSubmission) => {
    setSelectedSubmission(submission);
    setIsViewModalOpen(true);
  };

  const handleUploadClick = () => {
    setIsUploadModalOpen(true);
  };

  const renderContent = () => {
    if (!activeTab) return null;

    if (isLoading) {
      return (
        <div className={dashboardStyles.loaderDiv}>
          <img src={LoaderSvg} alt="loader" width={100} />
        </div>
      );
    }

    if (submissions?.length === 0) {
      const tabConfig = Object.values(TABS).find((tab) => tab.value === activeTab);
      if (tabConfig) {
        return (
          <EmptyContentState
            title={tabConfig.emptyState.title}
            description={tabConfig.emptyState.description}
            showUploadButton={tabConfig.emptyState.showUploadButton}
            onUploadClick={handleUploadClick}
          />
        );
      }
    }

    return (
      <div className={styles.submissionsGrid}>
        {submissions?.map((submission) => {
          const firstUrl = submission.urls[0];
          const fileUrl = typeof firstUrl === 'string' ? firstUrl : firstUrl?.fileUrl || '';
          const fileType = typeof firstUrl === 'string' ? '' : firstUrl?.type || '';

          return (
            <SubmissionCard
              key={submission._id}
              file={fileUrl}
              type={fileType}
              name={submission.name}
              submissionType={submission.type}
              date={dayjs(submission.createdAt).format('D MMMM, YYYY')}
              stage={activeTab}
              commentCount={submission.commentsCount}
              onView={() => handleView(submission)}
              onEdit={activeTab === 'pending' ? () => handleEdit(submission) : undefined}
              onDelete={activeTab !== 'approved' ? () => handleDelete(submission) : undefined}
              isResubmission={submission.isResubmitted}
            />
          );
        })}
      </div>
    );
  };

  const tabOptions = Object.values(TABS).map((tab) => ({
    value: tab.value,
    label: tab.label,
  }));

  const renderTabSelector = () => {
    if (isMobile) {
      return (
        <CustomSelect
          selectLabel={tabOptions.find((opt) => opt.value === activeTab)?.label || 'Select Status'}
          dropDownElement={(setOpened) => (
            <div className={styles.mobileDropdown}>
              {tabOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`${styles.mobileDropdownItem} ${option.value === activeTab ? styles.active : ''}`}
                  onClick={() => {
                    handleTabChange(option.value as ContentSubmissionStage);
                    setOpened(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        />
      );
    }

    return (
      <SlidingButton
        options={tabOptions}
        selected={activeTab}
        setSelected={(value) => handleTabChange(value as ContentSubmissionStage)}
        className={styles.submissionsSlider}
      />
    );
  };

  return (
    <DashboardWrapper
      heading="Submissions"
      rightHeaderComponent={
        submissions &&
        submissions.length > 0 && (
          <ButtonComponent
            className={styles.uploadButton}
            type="button"
            onClick={handleUploadClick}
            aria-label="Upload Content"
            variant="secondary"
          >
            <IconUpload size={20} />
            <span>Upload Content</span>
          </ButtonComponent>
        )
      }
    >
      <div className={styles.submissionsContainer}>
        <div className={styles.submissionsHeader}>{renderTabSelector()}</div>
        {renderContent()}
        <EditResubmitModal
          isOpen={isEditModalOpen || isResubmitModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setIsResubmitModalOpen(false);
            setSelectedSubmission(null);
          }}
          submission={selectedSubmission}
          isResubmit={isResubmitModalOpen}
        />

        <DeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSelectedSubmission(null);
          }}
          onConfirm={handleDeleteConfirm}
          isDeleting={deleteSubmissionMutation.isPending}
        />

        <ViewModal
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedSubmission(null);
          }}
          onResubmit={() => {
            if (selectedSubmission) {
              setIsResubmitModalOpen(true);
            }
          }}
          submission={selectedSubmission}
          comments={comments || []}
          isLoadingComments={isLoadingComments}
        />

        <UploadModal isOpen={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)} />
      </div>
    </DashboardWrapper>
  );
}
